/**
 * Multi-select component for managing user brand subscriptions
 */
import { useState, useEffect } from 'react';
import { Check, Save, Loader2 } from 'lucide-react';
import { useBrandsControllerGetBrands } from '@/generated/api/brands/brands';
import {
  useBrandSubscriptions,
  useSetBrandSubscriptions,
} from '../hooks/useAdminUsers';
import { haptic } from '@/shared/utils/telegram';

interface BrandSubscriptionSelectorProps {
  telegramUserId: string;
}

export function BrandSubscriptionSelector({
  telegramUserId,
}: BrandSubscriptionSelectorProps) {
  const [selectedBrands, setSelectedBrands] = useState<Set<string>>(new Set());
  const [initialized, setInitialized] = useState(false);

  const brandsQuery = useBrandsControllerGetBrands();
  const subscriptionsQuery = useBrandSubscriptions(telegramUserId);
  const setSubscriptionsMutation = useSetBrandSubscriptions();

  const availableBrands =
    brandsQuery.data?.data?.brands?.map((b) => b.brand) ?? [];

  // Initialize selected brands from current subscriptions
  useEffect(() => {
    if (subscriptionsQuery.data && !initialized) {
      setSelectedBrands(new Set(subscriptionsQuery.data.brands));
      setInitialized(true);
    }
  }, [subscriptionsQuery.data, initialized]);

  const currentSubscriptions = subscriptionsQuery.data?.brands ?? [];
  const hasChanges =
    initialized &&
    (selectedBrands.size !== currentSubscriptions.length ||
      [...selectedBrands].some((b) => !currentSubscriptions.includes(b)));

  const toggleBrand = (brand: string) => {
    haptic.impact('light');
    setSelectedBrands((prev) => {
      const next = new Set(prev);
      if (next.has(brand)) {
        next.delete(brand);
      } else {
        next.add(brand);
      }
      return next;
    });
  };

  const handleSave = async () => {
    try {
      haptic.impact('medium');
      await setSubscriptionsMutation.mutateAsync({
        telegramUserId,
        brands: [...selectedBrands],
      });
      haptic.notification('success');
    } catch {
      haptic.notification('error');
    }
  };

  const isLoading = brandsQuery.isLoading || subscriptionsQuery.isLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 size={20} className="animate-spin text-[var(--tg-theme-hint-color)]" />
      </div>
    );
  }

  if (availableBrands.length === 0) {
    return (
      <p className="text-xs text-[var(--tg-theme-hint-color)] py-2">
        No brands available
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="space-y-1.5 max-h-48 overflow-y-auto">
        {availableBrands.map((brand) => (
          <button
            key={brand}
            type="button"
            onClick={() => toggleBrand(brand)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${
              selectedBrands.has(brand)
                ? 'bg-[var(--tg-theme-link-color)]/10 border border-[var(--tg-theme-link-color)]/30'
                : 'bg-[var(--tg-theme-secondary-bg-color)] border border-transparent'
            }`}
          >
            <div
              className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                selectedBrands.has(brand)
                  ? 'bg-[var(--tg-theme-link-color)] text-white'
                  : 'bg-[var(--tg-theme-bg-color)] border border-black/10 dark:border-white/10'
              }`}
            >
              {selectedBrands.has(brand) && <Check size={14} />}
            </div>
            <span className="text-sm font-medium text-[var(--tg-theme-text-color)]">
              {brand}
            </span>
          </button>
        ))}
      </div>

      {hasChanges && (
        <button
          onClick={handleSave}
          disabled={setSubscriptionsMutation.isPending}
          className="w-full py-2.5 px-4 bg-[var(--tg-theme-link-color)] text-white rounded-xl text-xs font-bold uppercase tracking-wide hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Save size={14} />
          {setSubscriptionsMutation.isPending
            ? 'Saving...'
            : 'Save Subscriptions'}
        </button>
      )}
    </div>
  );
}
